<template>
  <div class="property-description">
    <FormGroup>
      <FormField :error="form.fieldErrors('description')">
        <Textarea rows="15" v-model="form.fields.description" :disabled="isGeneratingDescription" />
      </FormField>
    </FormGroup>

    <div class="controls">
      <IconButton
        :variant="'secondary'"
        :size="'small'"
        :icon="'copy'"
        :disabled="!form.fields.description || form.submitting || isGeneratingDescription"
        @click="copyDescription"
      />

      <Button :variant="'secondary'" :size="'small'" :loading="isGeneratingDescription" @click="showHighlightsModal">
        <Icon name="stars-03" size="extra-small" />
        <span>Improve</span>
      </Button>

      <Button :variant="'secondary'" :size="'small'" :loading="isGeneratingDescription" @click="generateDescription">
        <Icon name="refresh" size="extra-small" />
        {{ form.fields.description ? 'Regenerate' : 'Generate' }}
      </Button>
    </div>

    <TPropertyDetailsDescriptionHighlightsModal @highlights-updated="generateDescription" ref="highlightsModalRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import {
  Button,
  FormField,
  FormGroup,
  Icon,
  Textarea,
  IconButton,
  useForm,
  useAlertsStore,
} from '@tallo/design-system';

import { usePropertyStore } from '../../../stores/property.store';
import TPropertyDetailsDescriptionHighlightsModal from './property-description-highlights-modal.vue';

const highlightsModalRef = ref();
const alertsStore = useAlertsStore();
const propertyStore = usePropertyStore();
const isGeneratingDescription = ref(false);
const form = useForm<{ description: string }>({
  description: '',
});

async function save() {
  return await form.submit((fields) => propertyStore.saveDescription(fields.description));
}

function copyDescription() {
  navigator.clipboard
    .writeText(form.fields.description)
    .then(() => {
      alertsStore.success('Description copied to clipboard');
    })
    .catch((error) => {
      console.error('Failed to copy description: ', error);
    });
}

function showHighlightsModal() {
  highlightsModalRef.value?.modalRef?.show();
}

async function generateDescription() {
  isGeneratingDescription.value = true;
  propertyStore
    .generateDescription()
    .then((description) => populateDescription(description))
    .catch((error) => {
      // TODO replace with toast
      console.error('Failed to generate description: ', error);
    })
    .finally(() => {
      isGeneratingDescription.value = false;
    });
}

function populateDescription(description: string) {
  form.fields.description = description;
}

watch(() => propertyStore.property?.description!, populateDescription, { immediate: true });

defineExpose({ save, generateDescription, isGeneratingDescription, form });
</script>

<style scoped lang="scss">
.controls {
  margin-top: var(--t-spacing-sm);
  display: flex;
  justify-content: flex-end;
  gap: var(--t-spacing-sm);
}

.highlights-bar {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  margin-bottom: var(--t-spacing-sm);
}
</style>
